/* ==============================================================================
   Barra superior
   ==============================================================================
   Contexto atual à esquerda, usuário à direita.

   O menu do usuário fica AQUI e não na dock de propósito: a dock é navegação
   entre servidores, e "quem eu sou" não é um servidor. Misturar os dois é o que
   faz o avatar do Discord parecer um item da lista de comunidades.
   ============================================================================== */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Avatar } from '../components/Avatar.tsx';
import { GuildIcon } from '../components/GuildIcon.tsx';
import { Logo } from '../assets/brand/Logo.tsx';

export interface TopBarUser {
  id: string;
  displayName: string;
  username: string;
  status: 'online' | 'idle' | 'dnd' | 'offline';
  instanceAdmin?: boolean;
}

export interface TopBarProps {
  /**
   * Nome do canal aberto. Vazio esconde o bloco de título inteiro.
   *
   * Vazio é um estado LEGÍTIMO e não um descuido: numa conversa direta o nome de
   * quem está do outro lado é dito pelo cabeçalho da conversa, dentro do painel
   * dela, junto do `@usuário`, do estado e dos botões de chamada. Repeti-lo aqui
   * poria dois nomes de pessoa na mesma linha — o interlocutor à esquerda e o
   * dono da sessão à direita — sem nada que diga qual é qual.
   *
   * Antes esta prop caía em "Início" quando não havia canal, e era o pior dos
   * casos: um rótulo que nomeia um lugar, num produto em que o lugar é uma lista
   * de pessoas.
   */
  title: string;
  /** Linha secundária: tópico do canal, ou contagem de participantes. */
  subtitle?: string;
  /** Ícone antes do título: `#` para texto, alto-falante para voz. */
  icon?: ReactNode;
  /**
   * Servidor a que o canal pertence.
   *
   * Renderiza o ícone do servidor à ESQUERDA do nome do canal, com o nome do
   * servidor em linha secundária quando não há tópico. Não é redundante com a
   * barra de comando: ali o ícone diz "onde eu posso ir", aqui diz "onde eu
   * estou" — e a segunda pergunta é a que se faz depois de dez minutos de
   * conversa, quando o olho já não passa pela barra de baixo.
   *
   * Ausente = a barra fica exatamente como era (Início, conversa direta).
   */
  guildName?: string | undefined;
  guildIconUrl?: string | null | undefined;
  user: TopBarUser;
  /** Ações à direita, antes do avatar. */
  actions?: ReactNode;
  onOpenProfile: () => void;
  onLogout: () => void;
  onToggleAside?: () => void;
  asideCollapsed?: boolean;
}

export function TopBar({
  title,
  subtitle,
  icon,
  guildName,
  guildIconUrl,
  user,
  actions,
  onOpenProfile,
  onLogout,
  onToggleAside,
  asideCollapsed = false,
}: TopBarProps) {
  return (
    <div
      className="flex items-center justify-between gap-3 px-4"
      style={{ minHeight: 48 }}
    >
      {/* ------------------------------------------------------------------------
          Contexto
      ------------------------------------------------------------------------ */}
      <div className="flex min-w-0 items-center gap-2.5">
        {guildName !== undefined && (
          <GuildIcon
            name={guildName}
            iconUrl={guildIconUrl ?? null}
            size={28}
          />
        )}
        {icon && (
          <span aria-hidden="true" style={{ color: 'var(--color-text-tertiary)' }}>
            {icon}
          </span>
        )}
        <div className="min-w-0">
          {/*
            O `<h1>` só existe se houver título.

            Renderizar `<h1></h1>` vazio deixaria o marco de nível 1 da página
            sem texto — um leitor de tela anunciaria "cabeçalho nível 1" e nada
            depois, o que é pior do que a ausência: a pessoa espera conteúdo.
            Sem o elemento, o cabeçalho de nível 1 da tela passa a ser o da
            conversa, que é onde o nome de verdade está.
          */}
          {title !== '' && (
            <h1
              className="truncate font-semibold"
              style={{
                fontSize: 'var(--text-base)',
                letterSpacing: 'var(--tracking-tight)',
                color: 'var(--color-text-primary)',
              }}
            >
              {title}
            </h1>
          )}
          {/*
            Linha secundária: o tópico quando existe, senão o nome do servidor.
            Nunca os dois — a barra tem 48px, e duas linhas de metadado embaixo do
            título empurrariam o título para fora do centro óptico.
          */}
          {(subtitle ?? guildName) !== undefined && (
            <p
              className="truncate"
              style={{
                fontSize: 'var(--text-2xs)',
                color: 'var(--color-text-tertiary)',
              }}
            >
              {subtitle ?? guildName}
            </p>
          )}
        </div>
      </div>

      {/* ------------------------------------------------------------------------
          Ações e usuário
      ------------------------------------------------------------------------ */}
      <div className="flex shrink-0 items-center gap-1.5">
        {actions}

        {onToggleAside && (
          <IconButton
            label={asideCollapsed ? 'Mostrar membros' : 'Ocultar membros'}
            onClick={onToggleAside}
            pressed={!asideCollapsed}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="6" cy="5.6" r="2.2" stroke="currentColor" strokeWidth="1.4" />
              <path d="M2.2 13c0-2.1 1.7-3.4 3.8-3.4S9.8 10.9 9.8 13"
                    stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              <path d="M11.4 4.6a2 2 0 010 3.8M12.6 13c0-1.5-.4-2.4-1.2-3"
                    stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
          </IconButton>
        )}

        <UserMenu user={user} onOpenProfile={onOpenProfile} onLogout={onLogout} />

        {/* --------------------------------------------------------------------
            A marca, no canto
            --------------------------------------------------------------------
            Último elemento da barra, no extremo direito. Não fica à esquerda de
            propósito: aquele lado é CONTEXTO — o canal ou a conversa aberta, que
            muda a cada navegação. Uma marca fixa colada num rótulo que muda faz
            os dois competirem pela mesma leitura.

            Só o símbolo, sem o wordmark. A barra tem 48px de altura e o wordmark
            completo empurraria o nome do canal, que é a informação que muda e
            por isso importa mais.

            `opacity: 0.6` em repouso: é âncora de marca, não controle. Na
            opacidade cheia ela puxaria atenção de um elemento que não faz nada
            ao ser olhado — e o menu do usuário, que fica ao lado, é clicável.

            `title=""` a marca como decorativa. O leitor de tela já tem o título
            da janela e o `aria-label` da dock; anunciar "Concord" a cada foco na
            barra seria ruído numa interface usada por horas.
        -------------------------------------------------------------------- */}
        <span
          aria-hidden="true"
          className="mx-1 block shrink-0"
          style={{ width: 1, height: 18, background: 'var(--color-border-subtle)' }}
        />
        <span
          className="grid shrink-0 place-items-center transition-opacity"
          style={{ width: 24, height: 24, opacity: 0.6 }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0.6';
          }}
          title="Concord"
        >
          <Logo size={20} variant="color" title="" />
        </span>
      </div>
    </div>
  );
}

/* ==============================================================================
   Menu do usuário
   ============================================================================== */

function UserMenu({
  user,
  onOpenProfile,
  onLogout,
}: {
  user: TopBarUser;
  onOpenProfile: () => void;
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  /*
   * Fecha ao clicar fora e ao pressionar Escape.
   *
   * Os dois são necessários e por razões diferentes: clique fora é o gesto de
   * mouse esperado, Escape é o de teclado. Menu que só fecha clicando no botão
   * de novo prende quem navega por Tab.
   */
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 pl-2 pr-1 transition-colors"
        style={{
          height: 34,
          borderRadius: 'var(--radius-full)',
          background: open ? 'oklch(100% 0 0 / 0.07)' : 'transparent',
        }}
      >
        <div className="hidden flex-col items-end sm:flex">
          <span
            className="leading-tight"
            style={{
              fontSize: 'var(--text-xs)',
              fontWeight: 500,
              color: 'var(--color-text-primary)',
            }}
          >
            {user.displayName}
          </span>
          <span
            className="leading-tight"
            style={{
              fontSize: 'var(--text-2xs)',
              color: 'var(--color-text-tertiary)',
            }}
          >
            @{user.username}
          </span>
        </div>
        <Avatar
          name={user.displayName}
          seed={user.id}
          size={28}
          status={user.status}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="glass absolute right-0 py-1.5"
          style={{
            top: 'calc(100% + var(--spacing-2))',
            minWidth: 208,
            borderRadius: 'var(--radius-lg)',
            /*
             * `--shadow-lg` sozinho não bastava: ele é sombra PRETA, calibrada
             * para superfície sobre superfície, e sobre o fundo escuro deste tema
             * ela praticamente não existe — o menu ficava chapado contra a barra,
             * sem silhueta. A aresta de luz dá a borda, a sombra longa dá a
             * distância, e o brilho fraco de acento separa do que está atrás.
             */
            boxShadow:
              'var(--glass-edge), 0 18px 48px oklch(0% 0 0 / 0.55), var(--glow-soft)',
            zIndex: 'var(--z-dropdown)',
          }}
        >
          <div className="flex items-center gap-2.5 px-3 pb-2 pt-1">
            <Avatar name={user.displayName} seed={user.id} size={36} />
            <div className="min-w-0">
              <p
                className="truncate font-medium"
                style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--color-text-primary)',
                }}
              >
                {user.displayName}
              </p>
              <p
                className="truncate"
                style={{
                  fontSize: 'var(--text-2xs)',
                  color: 'var(--color-text-tertiary)',
                }}
              >
                @{user.username}
              </p>
            </div>
          </div>

          {user.instanceAdmin && (
            <div className="px-3 pb-2">
              <span
                className="inline-block font-medium uppercase"
                style={{
                  fontSize: 9,
                  letterSpacing: 'var(--tracking-wide)',
                  padding: '2px 6px',
                  borderRadius: 'var(--radius-full)',
                  background: 'var(--color-accent-subtle)',
                  color: 'var(--color-accent-on-subtle)',
                }}
              >
                Admin da instância
              </span>
            </div>
          )}

          <MenuDivider />

          <MenuItem
            onClick={() => {
              setOpen(false);
              onOpenProfile();
            }}
          >
            Perfil e preferências
          </MenuItem>

          <MenuDivider />

          <MenuItem
            danger
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            Sair
          </MenuItem>
        </div>
      )}
    </div>
  );
}

function MenuDivider() {
  return (
    <span
      aria-hidden="true"
      className="my-1 block"
      style={{ height: 1, background: 'var(--color-border-subtle)' }}
    />
  );
}

function MenuItem({
  children,
  onClick,
  danger = false,
}: {
  children: ReactNode;
  onClick: () => void;
  danger?: boolean;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="block w-full px-3 py-1.5 text-left transition-colors"
      style={{
        fontSize: 'var(--text-xs)',
        color: danger ? 'var(--color-danger-on-subtle)' : 'var(--color-text-secondary)',
        /* Luz translúcida e não `--color-surface-hover`: dentro de um painel de
           vidro, uma superfície opaca de hover corta um retângulo sólido no meio
           do material. A luz acompanha o que está atrás. */
        background: hover
          ? danger
            ? 'var(--color-danger-subtle)'
            : 'oklch(100% 0 0 / 0.07)'
          : 'transparent',
      }}
    >
      {children}
    </button>
  );
}

/** Botão de ícone da barra. `pressed` marca estado de alternância. */
export function IconButton({
  label,
  onClick,
  pressed,
  children,
}: {
  label: string;
  onClick: () => void;
  pressed?: boolean;
  children: ReactNode;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      className="grid place-items-center transition-colors"
      style={{
        width: 30,
        height: 30,
        borderRadius: 'var(--radius-md)',
        background: hover ? 'oklch(100% 0 0 / 0.07)' : 'transparent',
        color: pressed ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)',
      }}
    >
      {children}
    </button>
  );
}
